import { Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import { CreateInstanceRequest } from "../../../Core/Api/Types/create-instance-request";
import { WorkflowType } from "../../../Core/Api/Types/workflow-type";
import { workflowService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { True } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalRequestPurgeElementProps = {
  title?: string;
  assetIds: number[];
  onClose?: (props: CloseClbProps) => void;
};
export type ModalRequestPurgeProps = CommonModalProps<ModalRequestPurgeElementProps>;

const ModalRequestPurge = React.forwardRef<any, ModalRequestPurgeProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...modalProps } = props;
  const customSnackbar = useCustomSnackbar();
  const [commentValue, setCommentValue] = React.useState<string>();
  const [isCheckedDeleteChildren, setIsCheckedDeleteChildren] = React.useState<boolean>(false);
  const [isCheckedDeleteRequest, setIsCheckedDeleteRequest] = React.useState<boolean>(false);
  const isValid = useAppSelector(GetSectionIsValid("form-confirm", "sectionComment"));
  const commentLabel = t("modal:modalRequestPurge.body.letUsKnowYourPurgeReason");

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    HandlePurgeMF();
    onClose && onClose({ confirm: true });
    closeModal();
  };

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentValue(event.target.value);
  };

  const HandlePurgeMF = React.useCallback(() => {
    if (!commentValue) {
      customSnackbar(t("alert:warning.pleaseFillInComment"), "warning");
      return;
    }

    let inboundVars;
    if (isCheckedDeleteChildren || isCheckedDeleteRequest) {
      if (isCheckedDeleteChildren) inboundVars = { ["PURGE_CHILD_CLIPS"]: "true" };
      if (isCheckedDeleteRequest) inboundVars = { ...inboundVars, ["PURGE_DELETE_REQUEST_ON_SUCCESS"]: "true" };
    } else inboundVars = null;

    if (modalProps.assetIds.length == 1) {
      let request = new CreateInstanceRequest({
        assetId: modalProps.assetIds.find(True),
        annotation: commentValue,
        definitionType: WorkflowType.PurgeMediaFile,
        inboundVars: inboundVars,
      });

      workflowService.HandleCreateInstance(request).then(([message, type]) => {
        customSnackbar(message, type as "warning" | "error" | "success" | "primary" | undefined);
      });
    } else {
      const instancesRequests: CreateInstanceRequest[] = modalProps.assetIds.map((i) => ({
        assetId: i,
        annotation: commentValue,
        definitionType: WorkflowType.PurgeMediaFile,
        definitionId: 0,
        inboundVars: inboundVars,
      }));

      workflowService.CreateInstances(instancesRequests).then((response) => {
        if (response.success) customSnackbar(t("alert:success.createdPurgeRequests"), "success");
        else customSnackbar(response.message!, "error");
      });
    }
  }, [isCheckedDeleteChildren, isCheckedDeleteRequest, commentValue, modalProps.assetIds]);

  return (
    <Modal
      title={modalProps.title}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!isValid}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1}>
        <Typography variant="h6" component="span" color="text.secondary">
          {commentLabel !== undefined && commentLabel.length > 0 ? commentLabel : t("field:label.commentColon")}
        </Typography>
        <Stack spacing={2} mb={2}>
          <FormInputText
            multiline
            value={commentValue}
            onChange={updateInputValue}
            placeholder={t("field:placeholder.comment")}
            rows={3}
            validationProps={{
              formId: "form-confirm",
              formSectionId: "sectionComment",
              fieldId: "comment",
              initialState: false,
              required: true,
              minLength: 3,
            }}
          />
        </Stack>
        <Stack spacing={2}>
          <FormControlLabel
            sx={{ mt: 1.5, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={isCheckedDeleteRequest}
                onChange={(e) => setIsCheckedDeleteRequest(e.target.checked)}
              />
            }
            label={t("field:label.deleteRequestAfterPurge")}
          />
          <FormControlLabel
            sx={{ mt: 1.5, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={isCheckedDeleteChildren}
                onChange={(e) => setIsCheckedDeleteChildren(e.target.checked)}
              />
            }
            label={t("field:label.deleteAssociatedClips")}
          />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalRequestPurge;
